import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link, Navigate } from 'react-router-dom';
import {
  useCreateIntegration,
  useProviders,
} from '../../hooks/useIntegrations';
import {
  Button,
  Card,
  CardHeader,
  Checkbox,
  Input,
  MarkdownContent,
  ProviderLogo,
} from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import { useSpaceContext } from '../../contexts/SpaceContext';
import { useAuth } from '../../store/auth-context';
import { meetsRole } from '../../lib/space-roles';
import { isSlackIntegration } from '../../lib/slack-integration';
import SlackBotCredentialsPanel from './SlackBotCredentialsPanel';
import { routes } from '../../lib/routes';
import { getProviderFallback } from '../../lib/provider-instructions';
import { Copy, AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';
import type { Integration, SupportedEventType } from '../../types/integrations';
import SetupGuidePanel from './SetupGuidePanel';
import QuickSetupPanel from './QuickSetupPanel';
import BackLink from '../../components/navigation/BackLink';

export default function IntegrationForm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { spaceId, space, role } = useSpaceContext();
  const { user } = useAuth();
  const canManageBot = user?.role === 'admin' || meetsRole(role, 'admin');
  const { success, error: showError } = useToast();
  const createIntegration = useCreateIntegration();

  const provider = searchParams.get('provider');
  const { data: providers, isLoading: providersLoading } = useProviders();
  const providerInfo = provider ? providers?.find((p) => p.name === provider) : undefined;
  const providerDisplayName =
    providerInfo?.displayName ?? (provider ? getProviderFallback(provider).displayName : '');
  const availableEvents: SupportedEventType[] = providerInfo?.supportedEventTypes ?? [];

  const requiresUserSecret = providerInfo?.secretMode === 'provider-issued';
  const secretLabel = providerInfo?.secretField?.label ?? 'Webhook secret';
  const secretHelp =
    providerInfo?.secretField?.helpText ??
    'Paste the secret issued by the provider.';

  const [displayName, setDisplayName] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [createdIntegration, setCreatedIntegration] = useState<Integration | null>(null);

  // Pre-select every known event so the default is "receive everything".
  // `availableEvents` is only populated once the providers query resolves, so
  // we seed selection here instead of in useState's initializer.
  useEffect(() => {
    if (availableEvents.length > 0 && selectedEvents.length === 0) {
      setSelectedEvents(availableEvents.map((e) => e.eventType));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerInfo]);

  if (!provider) {
    return <Navigate to={routes.console.spaceIntegrations(spaceId)} replace />;
  }

  if (providersLoading && !providerInfo) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const toggleEvent = (event: string) => {
    setSelectedEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event],
    );
  };

  const selectAllEvents = () =>
    setSelectedEvents(availableEvents.map((e) => e.eventType));
  const clearAllEvents = () => setSelectedEvents([]);

  const allSelected =
    availableEvents.length > 0 && selectedEvents.length === availableEvents.length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!displayName.trim()) return;
    // Block submit if the user has a catalog but deselected everything —
    // an empty filter list would be indistinguishable from "all events" on
    // the backend, which is almost certainly not what they want.
    if (availableEvents.length > 0 && selectedEvents.length === 0) return;

    try {
      const result = await createIntegration.mutateAsync({
        provider,
        displayName: displayName.trim(),
        // "All selected" or "no catalog" → omit the filter so the backend
        // treats it as "receive everything". Otherwise send the subset.
        eventFilters: allSelected || availableEvents.length === 0 ? undefined : selectedEvents,
        // For provider-issued secrets the user can submit without one — the
        // backend issues a bridge secret and the user pastes the real
        // provider-issued value later via "Update Signing Secret".
        ...(requiresUserSecret && webhookSecret.trim()
          ? { webhookSecret: webhookSecret.trim() }
          : {}),
      });
      setCreatedIntegration(result);
      success('Integration created successfully');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create integration');
    }
  };

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      success(`${label} copied to clipboard`);
    } catch {
      showError('Failed to copy to clipboard');
    }
  };

  // Success state — show webhook credentials + setup guide
  if (createdIntegration) {
    return (
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Integration Created</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Configure your {providerDisplayName} webhook using the credentials below
          </p>
        </div>

        {/* Warning banner — only when Optr issues the secret. For
            provider-issued secrets the user already has it. */}
        {!requiresUserSecret && (
          <div className="rounded-lg border border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/20 p-4 flex gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Save the webhook secret now
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                The secret will not be shown again. If you lose it, you can rotate it from the integration detail page.
              </p>
            </div>
          </div>
        )}

        {/* Webhook URL */}
        <Card>
          <CardHeader title="Webhook URL" />
          <div className="flex items-center gap-3">
            <code className="text-sm bg-muted px-3 py-2 rounded-md flex-1 font-mono break-all">
              {createdIntegration.webhookUrl}
            </code>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleCopy(createdIntegration.webhookUrl, 'Webhook URL')}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </Card>

        <QuickSetupPanel
          provider={provider}
          providerDisplayName={providerDisplayName}
          documentationUrl={providerInfo?.documentationUrl}
          integration={createdIntegration}
        />

        {/* Webhook Secret — only when Optr issues it. */}
        {!requiresUserSecret && (
          <Card>
            <CardHeader title="Webhook Secret" />
            <div className="flex items-center gap-3">
              <code className="text-sm bg-muted px-3 py-2 rounded-md flex-1 font-mono break-all">
                {createdIntegration.webhookSecret}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopy(createdIntegration.webhookSecret!, 'Webhook secret')}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        )}

        {/* Slack bot tokens — paste the first workspace's token during setup */}
        {isSlackIntegration(createdIntegration) && (
          <SlackBotCredentialsPanel
            scope={{ kind: 'space', spaceId, spaceName: space?.name ?? spaceId }}
            canManage={canManageBot}
          />
        )}

        {/* Setup guide */}
        <SetupGuidePanel
          provider={provider}
          providerDisplayName={providerDisplayName}
          documentationUrl={providerInfo?.documentationUrl}
          integration={createdIntegration}
        />

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Link to={routes.console.spaceIntegrations(spaceId)}>
            <Button variant="secondary">Back to Integrations</Button>
          </Link>
          <Button onClick={() => navigate(routes.console.spaceIntegration(spaceId, createdIntegration.integrationId))}>
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Go to Integration
          </Button>
        </div>
      </div>
    );
  }

  // Create form
  return (
    <div className="space-y-6 max-w-4xl">
      <BackLink to={routes.console.spaceIntegrations(spaceId)}>Integrations</BackLink>

      {/* Header */}
      <div className="flex items-center gap-4">
        {provider && (
          <ProviderLogo
            slug={provider}
            displayName={providerDisplayName}
            className="h-12 w-12"
            fallbackClassName="text-sm"
          />
        )}
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Connect {providerDisplayName}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Set up a webhook integration to receive events from {providerDisplayName}
          </p>
        </div>
      </div>

      {/* Quick setup — rendered above the form so users know what's coming
          before they submit. Pre-creation tokens render as literal
          placeholders, signalling that submission is the next step. */}
      <QuickSetupPanel
        provider={provider}
        providerDisplayName={providerDisplayName}
        documentationUrl={providerInfo?.documentationUrl}
      />

      {/* Summary */}
      {providerInfo?.summary && (
        <Card>
          <CardHeader title="Overview" />
          <MarkdownContent size="sm">{providerInfo.summary}</MarkdownContent>
          {providerInfo.documentationUrl && (
            <a
              href={providerInfo.documentationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-500 mt-4"
            >
              Provider documentation <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </Card>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Configuration */}
        <Card>
          <CardHeader
            title="Configuration"
            description="Name your integration and optionally filter which events to receive"
          />
          <div className="space-y-4">
            <Input
              label="Display Name"
              value={displayName}
              onChange={(e: { target: { value: string } }) => setDisplayName(e.target.value)}
              placeholder="e.g. My Repository Webhooks"
              required
            />

            {requiresUserSecret && (
              <Input
                label={`${secretLabel} (optional)`}
                type="password"
                value={webhookSecret}
                onChange={(e: { target: { value: string } }) =>
                  setWebhookSecret(e.target.value)
                }
                placeholder="Leave blank to set later"
                hint={`${secretHelp} You can also leave this blank and paste the provider-issued value later via "Update Signing Secret" on the integration page.`}
              />
            )}

            {/* Event filters */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Event Filters</label>
              {availableEvents.length > 0 ? (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-muted-foreground">
                      All events are enabled by default. Uncheck any you don&apos;t want to receive.
                    </p>
                    <div className="flex gap-3 text-xs">
                      <button
                        type="button"
                        onClick={selectAllEvents}
                        className="text-primary-600 hover:text-primary-500"
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={clearAllEvents}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        Clear all
                      </button>
                    </div>
                  </div>
                  <div className="rounded-md border border-border divide-y">
                    {availableEvents.map((evt) => {
                      const id = `event-filter-${evt.eventType}`;
                      const checked = selectedEvents.includes(evt.eventType);
                      return (
                        <label
                          key={evt.eventType}
                          htmlFor={id}
                          className="flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-accent/50 transition-colors"
                        >
                          <Checkbox
                            id={id}
                            checked={checked}
                            onCheckedChange={() => toggleEvent(evt.eventType)}
                            className="mt-0.5"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-mono text-foreground break-all">
                              {evt.eventType}
                            </div>
                            {evt.description && (
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {evt.description}
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {selectedEvents.length === 0 && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                      Select at least one event, or click &quot;Select all&quot; to receive everything.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  You&apos;ll receive all events from this provider. Per-event filtering is not yet
                  available for {providerDisplayName}.
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Link to={routes.console.spaceIntegrations(spaceId)}>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Link>
          <Button
            type="submit"
            isLoading={createIntegration.isPending}
            disabled={
              !displayName.trim() ||
              (availableEvents.length > 0 && selectedEvents.length === 0)
            }
          >
            Create Integration
          </Button>
        </div>
      </form>
    </div>
  );
}
